import { CURRENT_SCHEMA_VERSION } from '../lib/storage/migrations';
import type { Profile, Routine, SyncedTree, Task } from '../lib/types';
import { uid } from '../lib/uid';

// First-launch seed. Per plan/02 §Offline behaviour: if a device boots with
// no local data and no sync code, generate a default family so the app is
// immediately usable.
//
// Two profiles by default (Nelli + Mikko). On v3 (see tasks/43) routines
// live in a top-level library. To keep the seed's initial UX unchanged —
// each kid gets their own editable Aamu/Ilta — we build four independent
// library routines and give each profile a pair of routineIds. A parent
// who wants to share edits between kids can assign a single routine to
// both via the Settings library screen; the seed intentionally doesn't
// pre-share so a rename on Nelli's Aamu doesn't surprise Mikko.

const DEFAULT_DEVICE_ID = 'local';

export function buildDefaultFamily(now: Date = new Date()): SyncedTree {
  const ts = now.toISOString();

  const nelliAamu = routine('☀️', 'Aamu', defaultAamuTasks(ts), 0, ts);
  const nelliIlta = routine('🌙', 'Ilta', defaultIltaTasks(ts), 1, ts);
  const mikkoAamu = routine('☀️', 'Aamu', defaultAamuTasks(ts), 2, ts);
  const mikkoIlta = routine('🌙', 'Ilta', defaultIltaTasks(ts), 3, ts);

  return {
    schemaVersion: CURRENT_SCHEMA_VERSION,
    syncCode: null,
    settings: { soundOn: true },
    meta: {
      createdAt: ts,
      lastWriteAt: ts,
      lastWriteByDeviceId: DEFAULT_DEVICE_ID,
    },
    profiles: [
      buildProfile('Nelli', '👧', '#fcd34d', 0, [nelliAamu.id, nelliIlta.id], ts),
      buildProfile('Mikko', '👦', '#93c5fd', 1, [mikkoAamu.id, mikkoIlta.id], ts),
    ],
    routines: [nelliAamu, nelliIlta, mikkoAamu, mikkoIlta],
    // Family theme library — seeded empty. Parents create entries via the
    // #47 editor; a fresh install has zero custom themes.
    customThemes: [],
  };
}

function buildProfile(
  name: string,
  avatarEmoji: string,
  color: string,
  sortIndex: number,
  routineIds: string[],
  ts: string,
): Profile {
  return {
    id: uid(),
    name,
    avatarEmoji,
    theme: { default: 'sky' },
    color,
    sortIndex,
    routineIds,
    createdAt: ts,
    updatedAt: ts,
  };
}

function defaultAamuTasks(ts: string): Task[] {
  return [
    task('🦷', 'PESE HAMPAAT', 'Hampaat kunnossa!', 0, ts),
    task('👕', 'PUE VAATTEET', 'Tyylikkäästi!', 1, ts),
    task('🥣', 'SYÖ AAMUPALA', 'Vatsa täynnä!', 2, ts),
    task('🎒', 'PAKKAA REPPU', 'Valmiina lähtöön!', 3, ts),
  ];
}

function defaultIltaTasks(ts: string): Task[] {
  return [
    task('🛁', 'KÄY SUIHKUSSA', 'Puhdas ja raikas!', 0, ts),
    task('🦷', 'PESE HAMPAAT', 'Hampaat kunnossa!', 1, ts),
    task('👕', 'PUE PYJAMA', 'Mukava olo!', 2, ts),
    task('📚', 'LUE KIRJA', 'Hyviä unia!', 3, ts),
  ];
}

function task(emoji: string, name: string, tag: string, sortIndex: number, ts: string): Task {
  return {
    id: uid(),
    name,
    emoji,
    tag,
    sortIndex,
    createdAt: ts,
    updatedAt: ts,
  };
}

function routine(
  emoji: string,
  name: string,
  tasks: Task[],
  sortIndex: number,
  ts: string,
): Routine {
  return {
    id: uid(),
    name,
    emoji,
    sortIndex,
    tasks,
    createdAt: ts,
    updatedAt: ts,
  };
}
